/**
 * Comment Synchronization Hook
 * 
 * Handles real-time comment synchronization via WebSocket:
 * - Broadcasts comment operations (add, update, delete)
 * - Receives comment updates from other users
 * - Handles sync failures with retry logic
 * 
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5
 */

import { useEffect, useRef } from 'react';
import { WebSocketClient } from '../services/websocket-client';
import { useDAWStore, type CommentMarker } from '../store/daw-store';

interface CommentOperation {
  type: 'comment_added' | 'comment_updated' | 'comment_deleted';
  projectId: string | null;
  comment: CommentMarker;
}

interface UseCommentSyncOptions {
  /**
   * WebSocket client instance
   */
  wsClient: WebSocketClient | null;
  
  /**
   * Enable comment synchronization
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook for synchronizing comments in real-time
 */
export function useCommentSync({ wsClient, enabled = true }: UseCommentSyncOptions) {
  const currentUserId = useDAWStore((state) => state.currentUserId);
  
  // Queue for failed operations (Requirements 5.5)
  const operationQueueRef = useRef<CommentOperation[]>([]);
  const retryTimerRef = useRef<number | null>(null);
  
  useEffect(() => {
    if (!enabled || !wsClient) return;
    
    // Handler for outgoing comment operations (Requirements 5.1, 5.2)
    const handleCommentOperation = (event: Event) => {
      const customEvent = event as CustomEvent<CommentOperation>;
      const operation = customEvent.detail;
      
      // Send via WebSocket
      if (wsClient.isConnected()) {
        wsClient.send({
          type: operation.type,
          projectId: operation.projectId,
          comment: operation.comment,
          userId: currentUserId,
          timestamp: Date.now(),
        });
      } else {
        // Queue operation if not connected (Requirements 5.5)
        console.warn('WebSocket not connected, queueing comment operation');
        operationQueueRef.current.push(operation);
        scheduleRetry();
      }
    };
    
    // Handler for incoming comment updates (Requirements 5.2, 5.4)
    const handleIncomingMessage = (data: any) => {
      // Ignore messages from self
      if (data.userId === currentUserId) return;
      
      // Handle comment operations
      if (data.type === 'comment_added') {
        // Add comment to store without triggering another broadcast
        const comment = data.comment as CommentMarker;
        useDAWStore.setState((state) => ({
          comments: [...state.comments, comment],
        }));
      } else if (data.type === 'comment_updated') {
        // Update comment in store
        const comment = data.comment as CommentMarker;
        useDAWStore.setState((state) => ({
          comments: state.comments.map((c) => 
            c.id === comment.id ? { ...c, ...comment } : c
          ),
        }));
      } else if (data.type === 'comment_deleted') {
        // Remove comment from store
        const comment = data.comment as CommentMarker;
        useDAWStore.setState((state) => ({
          comments: state.comments.filter((c) => c.id !== comment.id),
        }));
      }
    };
    
    // Handler for connection restored (Requirements 5.5)
    const handleConnectionRestored = () => {
      if (operationQueueRef.current.length > 0) {
        console.log(`Flushing ${operationQueueRef.current.length} queued comment operations`);
        const queue = [...operationQueueRef.current];
        operationQueueRef.current = [];
        
        queue.forEach((operation) => {
          wsClient.send({
            type: operation.type,
            projectId: operation.projectId,
            comment: operation.comment,
            userId: currentUserId,
            timestamp: Date.now(),
          });
        });
        
        // Clear retry timer
        if (retryTimerRef.current !== null) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
      }
    };
    
    // Schedule retry for queued operations (Requirements 5.5)
    const scheduleRetry = () => {
      if (retryTimerRef.current !== null) return;
      
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        if (wsClient.isConnected() && operationQueueRef.current.length > 0) {
          handleConnectionRestored();
        } else if (operationQueueRef.current.length > 0) {
          // Retry again in 5 seconds
          scheduleRetry();
        }
      }, 5000);
    };
    
    // Register event listeners
    window.addEventListener('comment-operation', handleCommentOperation);
    wsClient.on('message', handleIncomingMessage);
    wsClient.on('reconnected', handleConnectionRestored);
    
    // Cleanup
    return () => {
      window.removeEventListener('comment-operation', handleCommentOperation);
      wsClient.off('message', handleIncomingMessage);
      wsClient.off('reconnected', handleConnectionRestored);
      
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, [enabled, wsClient, currentUserId]);
  
  return {
    queuedOperations: operationQueueRef.current.length,
  };
}
